// Attaches the staff bearer token to every backend /api call that doesn't set its own
// Authorization header (fetch + axios), and signs out on a revoked/expired staff session.
import axios from 'axios';
import { getLinkedWs } from './workspaceLink';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STAFF_KEY = 'auth_token';

const isBackendApi = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.startsWith('/api/') && (u.origin === new URL(BACKEND).origin || u.origin === window.location.origin);
  } catch {
    return false;
  }
};

const isPublicAuthPath = (url) => /\/api\/(auth\/(email-login|verify-mfa|recovery-login)|client\/|onboarding\/public|esign\/public|id-verify\/public|hub\/public|public\/)/.test(url);

const handleStaffSessionLoss = (url) => {
  if (isPublicAuthPath(url) || !localStorage.getItem(STAFF_KEY)) return;
  localStorage.removeItem(STAFF_KEY);
  localStorage.removeItem('user_data');
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login?reason=session');
  }
};

export function installAuthInterceptors() {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    let opts = init;
    if (url && isBackendApi(url)) {
      const token = localStorage.getItem(STAFF_KEY);
      const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
      if (token && !headers.has('Authorization') && !isPublicAuthPath(url)) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (getLinkedWs() && !headers.has('X-Workspace-Id')) headers.set('X-Workspace-Id', getLinkedWs());
      opts = { ...init, headers };
    }
    const res = await nativeFetch(input, opts);
    if (res.status === 401 && url && isBackendApi(url) && (opts.headers?.get?.('Authorization') || '').includes(localStorage.getItem(STAFF_KEY) || '@@')) {
      handleStaffSessionLoss(url);
    }
    return res;
  };

  axios.interceptors.request.use((config) => {
    const url = config.url || '';
    const full = url.startsWith('http') ? url : `${config.baseURL || ''}${url}`;
    if (isBackendApi(full)) {
      const token = localStorage.getItem(STAFF_KEY);
      config.headers = config.headers || {};
      if (token && !config.headers.Authorization && !config.headers.authorization && !isPublicAuthPath(full)) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (getLinkedWs() && config.headers['X-Workspace-Id'] === undefined) config.headers['X-Workspace-Id'] = getLinkedWs();
    }
    return config;
  });
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const url = err?.config?.url || '';
      const auth = err?.config?.headers?.Authorization || '';
      if (err?.response?.status === 401 && isBackendApi(url.startsWith('http') ? url : `${BACKEND}${url}`) && auth.includes(localStorage.getItem(STAFF_KEY) || '@@')) {
        handleStaffSessionLoss(url);
      }
      return Promise.reject(err);
    },
  );
}
